"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, ArrowRight, UserCheck, Clock, Sparkles, Loader } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const benefits = [{
  title: "Direct Consultant Access",
  desc: "You work directly with me, Yassine Essaadi. No account managers or junior pass-throughs."
}, {
  title: "Bespoke SEO Strategy",
  desc: "Custom strategies tailored for UK, national, and international market visibility."
}, {
  title: "Transparent Deliverables",
  desc: "Practical technical fixes, content roadmaps, and honest, revenue-driven reporting."
}];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-muted rounded-xl border border-border text-center space-y-4">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold font-serif text-foreground">Consultation Requested</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          {successMessage || "Thank you. I have received your details and will review your website personally before contacting you within 24 business hours."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 cursor-pointer">
          Request Another Consultation
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Yassine Essaadi" className="bg-background h-11 px-3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Work email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="contact@ye-consultancy.com" className="bg-background h-11 px-3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://ye-consultancy.com" className="bg-background h-11 px-3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-medium text-base shadow-md transition-[color,background-color,box-shadow] cursor-pointer mt-2">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>

      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2">
        <span className="flex items-center gap-1">
          <Clock className="size-3.5 text-primary" /> 24h Response Time
        </span>
        <span className="flex items-center gap-1">
          <ShieldCheck className="size-3.5 text-primary" /> Strictly Confidential
        </span>
      </div>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="relative py-20 lg:py-28 bg-background border-t border-border overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10 max-w-6xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {}
          <div className="lg:col-span-7 space-y-8">
            <motion.div variants={itemVariants} className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium border border-border">
                <Sparkles className="size-3.5 text-accent" />
                <span>Direct Expert Access</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight leading-tight">
                Ready for Sustainable, Search-Engine Growth?
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl font-sans">
                When you choose YE Consultancy, you work directly with me — Yassine Essaadi. I provide hands-on, strategic SEO leadership that helps UK and international brands build authority, fix technical hurdles, and secure sustainable revenue.
              </p>
            </motion.div>

            {}
            <motion.div variants={itemVariants} className="space-y-4 pt-2">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3.5 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="mt-0.5 flex-shrink-0 size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <CheckCircle2 className="size-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-base font-serif">{benefit.title}</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>)}
            </motion.div>

            {}
            <motion.div variants={itemVariants} className="pt-4 border-t border-border flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full overflow-hidden bg-muted border border-border flex-shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_headshot_thumb.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_headshot_thumb.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_headshot_thumb.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="size-full object-cover" loading="lazy" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Yassine Essaadi</p>
                  <p className="text-xs">Independent SEO Consultant</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span>Prefer email?</span>
                <a href="mailto:contact@ye-consultancy.com" className="text-primary font-medium hover:underline">
                  contact@ye-consultancy.com
                </a>
              </div>

              <Button variant="outline" size="sm" asChild className="cursor-pointer">
                <Link to="/ContactPage">
                  View Full Contact Page
                  <ArrowRight className="ml-1.5 size-3.5" />
                </Link>
              </Button>
            </motion.div>
          </div>

          {}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-xl rounded-2xl relative overflow-hidden">
              <div className="h-1.5 w-full bg-primary" />
              <CardContent className="p-6 md:p-8 space-y-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                    <UserCheck className="size-4" />
                    <span>Free 30-Min Strategy Call</span>
                  </div>
                  <h3 className="text-2xl font-bold font-serif text-card-foreground">
                    Book Your SEO Consultation
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Share your domain details for a tailored, personal review of your organic opportunities.
                  </p>
                </div>

                <FormProvider formId="consultation-cta-form" sectionName="consultation-cta" defaultValues={{
                name: "",
                email: "",
                website: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! I will review your site and contact you shortly.">
                  <ConsultationFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}